import React, { useEffect, useState } from "react";
import { MarqueeSelection } from "@fluentui/react/lib/MarqueeSelection";
import { DetailsList, DetailsListLayoutMode, IColumn, Selection, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { markSortableColumns } from '../shared/components/table/columnDrag';
import { AcceptIcon, ProviderConsumer as FluentUIThemeConsumer, Text } from '@fluentui/react-northstar';
import { ThemeColorScheme } from "../shared/common/TeamsTheme";
import { EnumSortOrderType } from "../tikit/toolbar/CustomViewContextProvider";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { truncateString } from "../tikit/MarkdownHelper";
import Markdown from 'react-markdown';
import { _getUserAvatar } from "../shared/components/table/ClassDetailList";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { KnowledgeListMenu } from "./KnowledgeListMenu";
import { ScopePermissionPeopleComponent } from "../Rbac/ScopePermissionPeopleComponent";
import { availableToColumnWidth } from "../Rbac/AvailableTo/availableToWidth";
import { getCachedFeature } from "../shared/cache/FeatureCache";
import { LICENSE_FEATURE } from "../shared/utils/constants";
import { CatalogImage } from "../shared/components/Wysiwyg/CatalogImage";
import { catalogImageId } from "../shared/components/Wysiwyg/catalogImages";
import { listPreview } from "./listPreview";
import { ClampedPreview } from "./ClampedPreview";

const kbStyle = {
  whiteSpace: 'normal',
  wordBreak: 'break-word'
};

const listThumbnail = { maxHeight: 64, maxWidth: 120, borderRadius: 4 };

// The page's base styles reset every heading to body text, so H2 read exactly like a normal line. The list is a
// compact preview: a heading is bold, and H1 a little larger, rather than full size. H3-H6 are no longer offered by
// the editor; answers saved with one read as H2.
const listHeading = (fontSize: string) => (props) => {
  const { node, level, ...rest } = props;
  const Tag = node.tagName;
  return <Tag style={{ ...kbStyle, fontSize, fontWeight: 600, margin: 0 }} {...rest} />;
};

const kbOptions = {
  h1: listHeading('1.25em'),
  h2: listHeading('1em'),
  h3: listHeading('1em'),
  h4: listHeading('1em'),
  h5: listHeading('1em'),
  h6: listHeading('1em'),
  img(props) {
    const { node, ...rest } = props;
    const { title, ...image } = rest;
    return catalogImageId(image.src)
      ? <CatalogImage {...image} thumbnail style={listThumbnail} />
      : <img {...image} alt={image.alt ?? ''} style={listThumbnail} />;
  },
  a(props) {
    const { node, ...rest } = props;
    // In the link colour, not just underlined - otherwise a link reads the same as underlined text.
    return <a target="_blank" style={{ ...kbStyle, textDecoration: 'underline', color: 'var(--mgt-theme-brand-foreground, #6264a7)' }} {...rest} />;
  },
  ol(props) {
    const { node, ...rest } = props;
    return <ol className='defaultStyle' {...rest} />;
  },
  ul(props) {
    const { node, ...rest } = props;
    return <ul className='defaultStyle' {...rest} />;
  },
  div(props) {
    const { node, ...rest } = props;
    return <div style={kbStyle} {...rest} />;
  },
  span(props) {
    const { node, ...rest } = props;
    return <span style={kbStyle} {...rest} />;
  },
  p(props) {
    const { node, ...rest } = props;
    return <p style={kbStyle} {...rest} />;
  }
};
const questionsFormatter = (questions: string[]) => {
  const firstFiveQuestions = questions.slice(0, 5);
  const remainingQuestions = questions.slice(5);

  return (
    <>
      {firstFiveQuestions.map((q, i) => (<div><Text content={q} color="brand" /></div>))}
      {remainingQuestions.length > 0 && (<div><Text content="..." /></div>)}
    </>
  );
}
const getUserAvatar = (user: any) => {
  if (!user)
    return '';
  return (
    <div>
      {_getUserAvatar(user.Email, user.FullName)}
      &nbsp;
      {user.FullName}
    </div>
  );
}
// Available To lists people and groups, and options is the row menu - neither has an order to sort by.
const NON_SORTABLE_COLUMNS = ['availableto', 'options'];
const AVAILABLE_TO_MIN_WIDTH = 130;
const _onColumnClick = (onColumnClickCallback: (column: IColumn) => void) => (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
  if (NON_SORTABLE_COLUMNS.some(x => x == column.key)) return;

  onColumnClickCallback(column);
};

const KnowledgeDetailList = (props) => {
  const [selection, setSelection] = useState<Selection | undefined>();
  const [columns, setColumns] = useState([] as IColumn[]);
  const [scopingEnabled, setScopingEnabled] = useState(false);
  const { t, i18n } = useTranslation();
  const history = useHistory();
  
  useEffect(() => {
    const _selection: Selection = new Selection({ onSelectionChanged: () => { } });
    setSelection(_selection);

    setColumns(kbColumns());
  }, [props.rows])

  useEffect(()=>{
      (async () => {
        const data = await getCachedFeature(LICENSE_FEATURE.PermissionScoping);
        setScopingEnabled(data);
      })();
  },[props.isUserLicensed]);

  // Both halves of the fix need this number: the column reserves it as its width,
  // and each cell collapses its buckets to fit inside it.
  const availableToCellWidth = availableToColumnWidth(props.rows, AVAILABLE_TO_MIN_WIDTH);

  const phraseClicked = (item) => {
    if (props.isUserLicensed)
      history.push(`/servicecatalog/${item.id}`);
    else {
      let templateId = null;
      const match = /^ApplyTemplate:(?<id>\d+)$/i.exec(item.answer);
      if (match)
        templateId = parseInt(match.groups['id'], 10);

      window.open(`${window.location.protocol}//${window.location.host}/knowledge/${(match != null ? `template/${templateId}` : `article/${item.id}`)}`, "_blank");
    }
  }
  const renderRecord = (key: string) => (item) => {
    switch (key) {
      case 'questions':
        return <div onClick={() => { phraseClicked(item); }} className="cursor-pointer">{questionsFormatter(item.questions)}</div>
      case 'answer':
        return item.answer.match(/^ApplyTemplate:\d+$/gi) ? (
          (props.templates.filter(x => `ApplyTemplate:${x.Id}` === item.answer).length > 0 ? 
            (<Text content={`Template: ${props.templates.filter(x => `ApplyTemplate:${x.Id}` === item.answer)[0].Name}`} />) : 
            (<Text content={truncateString(item.answer, 350)} style={{ wordBreak: 'break-word' }} />))
        ) : (
          ((preview) => (
            <ClampedPreview more={preview.cut}>
              <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={kbOptions}>
                {preview.text}
              </Markdown>
            </ClampedPreview>
          ))(listPreview(item.answer))
        )
      case 'requestOffering':
        return <span>{item.enableOffering ? item.requestOffering : ""}</span>;
      case 'serviceOffering':
        return <span>{item.enableOffering ? item.serviceOffering : ""}</span>;
      case 'status':
        return <span>{item.status ? item.status.replace(/([a-z])([A-Z])/g, '$1 $2') : item.status}</span>
      case 'order': 
        return <span>{item.enableOffering ? item.order : ""}</span>
      case 'createdById':
      case 'modifiedById':
        return <span>{getUserAvatar(props.users?.find(u => u.Id == item[key]) ?? null)}</span>;
      case 'agentOnly':
        return item.agentOnly ? <AcceptIcon style={{ paddingLeft: '25px' }} /> : <></>;
      case 'lastUpdatedDateTime':
      case 'createdDateTime':
        return <span>{item[key] ? DateTime.fromISO(item[key]).setLocale(i18n.language).toLocaleString(DateTime.DATETIME_SHORT) : ''}</span>;
      case 'availableto':
        return <ScopePermissionPeopleComponent scopedMembers={item.ScopedMembers} maxWidth={availableToCellWidth}/>;
      case 'options':
        let templateId = null;
        const match = /^ApplyTemplate:(?<id>\d+)$/i.exec(item.answer);
        if (match)
          templateId = parseInt(match.groups['id'], 10);

        return <KnowledgeListMenu id={item.id} templateId={templateId} isTemplate={match != null} isUserLicensed={props.isUserLicensed} phrase={item.questions?.[0]} />;
    }
  };

  // DetailsList's justified layout hands a column with no maxWidth the whole of
  // the leftover width in one step, walking left to right, so exactly one column
  // should be left uncapped and it decides where the slack on a wide monitor ends
  // up. Response is that column. Available To used to be, which is what made it
  // run halfway across a large screen to show the word "Everyone". Note the last
  // column is a special case - it takes the remainder whatever its maxWidth says -
  // so capping everything instead would blow out the row menu. #35321
  const kbColumns = (): IColumn[] => {

    // Sizing Available To to its content is a minWidth job, not a maxWidth one. Only
    // minWidth is reserved for every column up front; a maxWidth merely permits growth
    // later, and that growth is handed out left to right until it runs out - so Response
    // above would take the slack first and this column would never reach its own content.
    // Fixing both ends pins it to exactly what the cell draws. #35321
    const columns = [
      {
        key: 'questions', name: t('knowledge.knowledge-table.phrases'), minWidth: 150, maxWidth: 300, isMultiline: true, onRender: renderRecord('questions'),
        isSorted: props.sortOrder.orderBy === 'questions' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'questions' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'answer', name: t('knowledge.knowledge-table.response'), minWidth: 250, isMultiline: true, onRender: renderRecord('answer'),
        isSorted: props.sortOrder.orderBy === 'answer' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'answer' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'requestOffering', name: t('knowledge.knowledge-table.request-offering'), minWidth: 125, maxWidth: 150, isMultiline: true, onRender: renderRecord('requestOffering'),
        isSorted: props.sortOrder.orderBy === 'requestOffering' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'requestOffering' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'serviceOffering', name: t('knowledge.knowledge-table.service-offering'), minWidth: 125, maxWidth: 150, isMultiline: true, onRender: renderRecord('serviceOffering'),
        isSorted: props.sortOrder.orderBy === 'serviceOffering' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'serviceOffering' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'order', name: t('knowledge.knowledge-table.order'), minWidth: 50, maxWidth: 50, isMultiline: false, onRender: renderRecord('order'),
        isSorted: props.sortOrder.orderBy === 'order' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'order' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'status', name: t('knowledge.knowledge-table.status'), minWidth: 50, maxWidth: 50, isMultiline: true, onRender: renderRecord('status'),
        isSorted: props.sortOrder.orderBy === 'status' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'status' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'createdById', name: t('knowledge.knowledge-table.createdBy'), minWidth: 130, maxWidth: 145, isMultiline: true, onRender: renderRecord('createdById'),
        isSorted: props.sortOrder.orderBy === 'createdById' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'createdById' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'modifiedById', name: t('knowledge.knowledge-table.modifiedBy'), minWidth: 130, maxWidth: 145, isMultiline: true, onRender: renderRecord('modifiedById'),
        isSorted: props.sortOrder.orderBy === 'modifiedById' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'modifiedById' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'availableto', name: t('scoping.available-to'), minWidth: availableToCellWidth, maxWidth: availableToCellWidth, isMultiline: true, onRender: renderRecord('availableto'),
        isSorted: null, isSortedDescending: false,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'createdDateTime', name: t('knowledge.knowledge-table.created'), minWidth: 125, maxWidth: 125, isMultiline: true, onRender: renderRecord('createdDateTime'),
        isSorted: props.sortOrder.orderBy === 'createdDateTime' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'createdDateTime' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'lastUpdatedDateTime', name: t('knowledge.knowledge-table.lastModified'), minWidth: 125, maxWidth: 125, isMultiline: true, onRender: renderRecord('lastUpdatedDateTime'),
        isSorted: props.sortOrder.orderBy === 'lastUpdatedDateTime' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'lastUpdatedDateTime' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'agentOnly', name: t('knowledge.knowledge-table.agent-only'), minWidth: 75, maxWidth: 75, isMultiline: true, onRender: renderRecord('agentOnly'),
        isSorted: props.sortOrder.orderBy === 'agentOnly' ? true : null, isSortedDescending: props.sortOrder.orderBy === 'agentOnly' && props.sortOrder.orderType === EnumSortOrderType.desc,
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false
      },
      {
        key: 'options', name: ' ', minWidth: 50, maxWidth: 50, isMultiline: true, onRender: renderRecord('options'),
        onColumnClick: _onColumnClick(props.onColumnClick), isResizable: false, isCollapsible: false, isMenuOpen: true
      },
    ];

    const showAvailableToColumn = props.isUserLicensed && scopingEnabled;
    return markSortableColumns(
      columns.filter(x=> showAvailableToColumn || (!showAvailableToColumn && x.key!="availableto")),
      column => !NON_SORTABLE_COLUMNS.includes(column.key)
    );
  }

  return selection ? (<FluentUIThemeConsumer render={(globalTheme) => (
    <div className={`py-3.5 px-2.5 min-h-32 main-ticket-list`} style={{ backgroundColor: 'var(--mgt-theme-background2)', ...ThemeColorScheme(globalTheme.siteVariables) }}>
      <MarqueeSelection selection={selection}>
        <DetailsList
          items={props.rows}
          compact={false}
          columns={columns}
          selectionMode={SelectionMode.none}
          setKey='multiple'
          layoutMode={DetailsListLayoutMode.justified}
          isHeaderVisible={true}
          selection={selection}
          selectionPreservedOnEmptyClick={true}
          enterModalSelectionOnTouch={true}
          ariaLabelForSelectionColumn='Toggle selection'
          ariaLabelForSelectAllCheckbox='Toggle selection for all items'
          checkButtonAriaLabel='select row'
        />
      </MarqueeSelection>
    </div>
  )} />) : (<></>);
};


export default KnowledgeDetailList;